import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PRIORITIES = ["Direct", "HOT", "Rush", "Regular", "Sameday", "NextDay"];

const today = () => new Date().toISOString().slice(0, 10);

export default function CreateDeliveryForm({ accounts, drivers, onCreated }) {
  const [f, setF] = useState({
    account_id: accounts.find(a => a.account_code === "CASH")?.id || accounts[0]?.id || "",
    po_number: "", booking_date: today(), delivery_date: today(),
    service_priority: "Regular", weight_kg: 1, pieces: 1,
    package_description: "", special_instructions: "",
    walk_name: "", walk_phone: "", walk_email: "",
    pickup_addr: "", pickup_name: "", pickup_phone: "", pickup_instr: "", pickup_lat: null, pickup_lng: null,
    dropoff_addr: "", dropoff_name: "", dropoff_phone: "", dropoff_instr: "", dropoff_lat: null, dropoff_lng: null,
    base_rate: 25, weight_surcharge: 0, fuel_surcharge_pct: 15,
    waiting_minutes: 0, waiting_rate: 1, gst_rate: 5,
    driver_id: "",
  });
  const [busy, setBusy] = useState(false);
  const account = accounts.find(a => a.id === f.account_id);
  const isCash = account?.type === "cash";

  const est = () => {
    const wait = Number(f.waiting_minutes) * Number(f.waiting_rate);
    const preSub = Number(f.base_rate) + Number(f.weight_surcharge) + wait;
    const fuel = preSub * (Number(f.fuel_surcharge_pct) / 100);
    const sub = preSub + fuel;
    const gst = sub * (Number(f.gst_rate) / 100);
    return { sub: sub.toFixed(2), gst: gst.toFixed(2), total: (sub + gst).toFixed(2), fuel: fuel.toFixed(2) };
  };
  const e = est();

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const payload = {
        account_id: f.account_id, po_number: f.po_number || null,
        booking_date: f.booking_date, delivery_date: f.delivery_date,
        service_priority: f.service_priority,
        pickup: { address: f.pickup_addr, contact_name: f.pickup_name, contact_phone: f.pickup_phone, instructions: f.pickup_instr, lat: f.pickup_lat, lng: f.pickup_lng },
        dropoff: { address: f.dropoff_addr, contact_name: f.dropoff_name, contact_phone: f.dropoff_phone, instructions: f.dropoff_instr, lat: f.dropoff_lat, lng: f.dropoff_lng },
        weight_kg: Number(f.weight_kg), pieces: Number(f.pieces),
        package_description: f.package_description, special_instructions: f.special_instructions,
        pricing: {
          base_rate: Number(f.base_rate), weight_surcharge: Number(f.weight_surcharge),
          fuel_surcharge_pct: Number(f.fuel_surcharge_pct),
          waiting_minutes: Number(f.waiting_minutes), waiting_rate: Number(f.waiting_rate),
          gst_rate: Number(f.gst_rate),
        },
        driver_id: f.driver_id || null,
      };
      if (isCash) payload.walk_in_customer = { name: f.walk_name, phone: f.walk_phone, email: f.walk_email };
      const { data } = await api.post("/deliveries", payload);
      const d = data.delivery;
      const msg = d.driver_name
        ? `Booked ${d.delivery_number} — assigned to ${d.driver_number || ""} ${d.driver_name}`.trim()
        : `Booked ${d.delivery_number} — unassigned (assign a driver from the list)`;
      toast.success(msg, { duration: 5000 });
      onCreated?.(d);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const Field = ({ label, children }) => (
    <div className="space-y-1"><Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">{label}</Label>{children}</div>
  );
  const cls = "h-11 bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm text-foreground placeholder:text-slate-400";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="create-delivery-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Account">
          <Select value={f.account_id} onValueChange={(v) => setF({ ...f, account_id: v })}>
            <SelectTrigger className={cls} data-testid="account-select"><SelectValue/></SelectTrigger>
            <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_code} — {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Service Priority">
          <Select value={f.service_priority} onValueChange={(v) => setF({ ...f, service_priority: v })}>
            <SelectTrigger className={cls} data-testid="priority-select"><SelectValue/></SelectTrigger>
            <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      {isCash && (
        <div className="border border-dashed border-brand-green p-3 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green">CASH / Walk-in Customer</div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Name" required value={f.walk_name} onChange={(e)=>setF({...f,walk_name:e.target.value})} className={cls} data-testid="walkin-name"/>
            <Input placeholder="Phone" value={f.walk_phone} onChange={(e)=>setF({...f,walk_phone:e.target.value})} className={cls} data-testid="walkin-phone"/>
            <Input placeholder="Email" value={f.walk_email} onChange={(e)=>setF({...f,walk_email:e.target.value})} className={cls} data-testid="walkin-email"/>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Field label="PO #"><Input value={f.po_number} onChange={(e)=>setF({...f,po_number:e.target.value})} className={cls} data-testid="po-input"/></Field>
        <Field label="Booking Date"><Input type="date" required value={f.booking_date} onChange={(e)=>setF({...f,booking_date:e.target.value})} className={cls}/></Field>
        <Field label="Delivery Date"><Input type="date" required value={f.delivery_date} onChange={(e)=>setF({...f,delivery_date:e.target.value})} className={cls}/></Field>
      </div>

      <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-brand-success">↑ Pickup</div>
        <AddressAutocomplete
          required
          value={f.pickup_addr}
          onChange={(addr, ex) => setF({ ...f, pickup_addr: addr, pickup_lat: ex.lat ?? null, pickup_lng: ex.lng ?? null })}
          placeholder="Business name or address (BC)"
          testId="pickup-address"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact Name" value={f.pickup_name} onChange={(e)=>setF({...f,pickup_name:e.target.value})} className={cls}/>
          <Input placeholder="Contact Phone" value={f.pickup_phone} onChange={(e)=>setF({...f,pickup_phone:e.target.value})} className={cls}/>
        </div>
        <Input placeholder="Pickup instructions" value={f.pickup_instr} onChange={(e)=>setF({...f,pickup_instr:e.target.value})} className={cls}/>
      </div>

      <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">↓ Delivery</div>
        <AddressAutocomplete
          required
          value={f.dropoff_addr}
          onChange={(addr, ex) => setF({ ...f, dropoff_addr: addr, dropoff_lat: ex.lat ?? null, dropoff_lng: ex.lng ?? null })}
          placeholder="Business name or address (BC)"
          testId="dropoff-address"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact Name" value={f.dropoff_name} onChange={(e)=>setF({...f,dropoff_name:e.target.value})} className={cls}/>
          <Input placeholder="Contact Phone" value={f.dropoff_phone} onChange={(e)=>setF({...f,dropoff_phone:e.target.value})} className={cls}/>
        </div>
        <Input placeholder="Delivery instructions" value={f.dropoff_instr} onChange={(e)=>setF({...f,dropoff_instr:e.target.value})} className={cls}/>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Weight (kg)"><Input type="number" step="0.1" min="0" required value={f.weight_kg} onChange={(e)=>setF({...f,weight_kg:e.target.value})} className={cls} data-testid="weight-input"/></Field>
        <Field label="Pieces"><Input type="number" min="1" required value={f.pieces} onChange={(e)=>setF({...f,pieces:e.target.value})} className={cls}/></Field>
        <Field label="Package Desc"><Input value={f.package_description} onChange={(e)=>setF({...f,package_description:e.target.value})} className={cls}/></Field>
      </div>
      <Field label="Special Instructions"><Textarea value={f.special_instructions} onChange={(e)=>setF({...f,special_instructions:e.target.value})} className="bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm placeholder:text-slate-400" data-testid="special-instr"/></Field>

      <div className="border-2 border-brand-green/50 p-3 space-y-2 bg-brand-green/5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green">Pricing Breakdown</div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Base Rate ($)"><Input type="number" step="0.01" value={f.base_rate} onChange={(e)=>setF({...f,base_rate:e.target.value})} className={cls} data-testid="base-rate"/></Field>
          <Field label="Weight Surch ($)"><Input type="number" step="0.01" value={f.weight_surcharge} onChange={(e)=>setF({...f,weight_surcharge:e.target.value})} className={cls}/></Field>
          <Field label="Fuel %"><Input type="number" step="0.1" value={f.fuel_surcharge_pct} onChange={(e)=>setF({...f,fuel_surcharge_pct:e.target.value})} className={cls}/></Field>
          <Field label="Wait Min"><Input type="number" step="1" value={f.waiting_minutes} onChange={(e)=>setF({...f,waiting_minutes:e.target.value})} className={cls}/></Field>
          <Field label="Wait $/min"><Input type="number" step="0.01" value={f.waiting_rate} onChange={(e)=>setF({...f,waiting_rate:e.target.value})} className={cls}/></Field>
          <Field label="GST %"><Input type="number" step="0.1" value={f.gst_rate} onChange={(e)=>setF({...f,gst_rate:e.target.value})} className={cls}/></Field>
        </div>
        <div className="font-mono text-xs grid grid-cols-4 gap-2 pt-2 border-t border-dashed border-border">
          <div><div className="text-[10px] uppercase text-muted-foreground">Fuel</div><div>${e.fuel}</div></div>
          <div><div className="text-[10px] uppercase text-muted-foreground">Subtotal</div><div>${e.sub}</div></div>
          <div><div className="text-[10px] uppercase text-muted-foreground">GST</div><div>${e.gst}</div></div>
          <div><div className="text-[10px] uppercase text-brand-green">Total</div><div className="text-brand-green font-bold" data-testid="preview-total">${e.total}</div></div>
        </div>
      </div>

      <Field label="Assign Driver (optional)">
        <Select value={f.driver_id} onValueChange={(v)=>setF({...f,driver_id:v})}>
          <SelectTrigger className={cls} data-testid="driver-select"><SelectValue placeholder="Leave unassigned"/></SelectTrigger>
          <SelectContent>
            {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.driver_number || "—"} {d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>

      <Button type="submit" disabled={busy} className="w-full h-12 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold" data-testid="submit-delivery-btn">
        {busy ? "Booking..." : "Book Delivery"}
      </Button>
    </form>
  );
}
